import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Popper from '@mui/material/Popper';
import {styled} from '@mui/material/styles';
import clsx from 'clsx';
import {FC, ReactElement, useState} from 'react';
import {MenuList} from '../Menu/MenuList';
import {dropdownCssClassNames} from './dropdownCssClassNames';
import {DropdownItem} from './DropdownItem';
import {useDropdownContext} from './hooks/useDropdownContext';
import {DropdownMenuProps, FuelPlacement} from './types';
import {DropdownMenuWrapper} from './utils/DropdownMenuWrapper';
import {getMenuAlignment} from './utils/getMenuAlignment';

const transforms = {
    top: 'bottom center',
    'top-start': 'bottom left',
    'top-end': 'bottom right',

    bottom: 'top center',
    'bottom-start': 'top left',
    'bottom-end': 'top right',

    left: 'right center',
    'left-start': 'right top',
    'left-end': 'right bottom',

    right: 'left center',
    'right-start': 'left top',
    'right-end': 'left bottom'
};

const DropdownMenuPopper = styled(Popper)({
    zIndex: 1300
});

export const DropdownMenu: FC<DropdownMenuProps> = ({
    children, popperModifiers: userModifiers = [], placement = 'bottom-start', align, flip,

    // flyout props
    items, hasDivider, className,

    ...props
}) => {
    const {
        open,
        anchorRef,
        handleClose,
        handleListKeyDown,
        dropdownMenuId
    } = useDropdownContext('Menu');

    const [menuIndex, setMenuIndex] = useState<number>(0);

    const resolvedPlacement = align ? getMenuAlignment(align) : placement;

    const resetChosenMenuItem = (): void => {
        setMenuIndex(0);
    };

    const handleTransitionExited = (): void => {
        resetChosenMenuItem();
    };

    const renderFlyoutContent = (): ReactElement | null => {
        const currentItem = typeof menuIndex === 'number' && Array.isArray(items) && items.length > 0 ?
            items[menuIndex] :
            undefined;

        if (currentItem && currentItem.FlyoutContent) {
            const FlyoutContent = currentItem.FlyoutContent;

            return (
                <FlyoutContent
                    hasDivider={hasDivider}
                    items={currentItem.subNavigationItems}
                    menuTitle={currentItem.label}
                />
            );
        }

        return null;
    };

    const popperModifiers = [
        {
            name: 'flip',
            enabled: Boolean(flip),
            options: {
                altBoundary: true,
                rootBoundary: 'document',
                padding: 16
            }
        },
        {
            name: 'preventOverflow',
            options: {
                padding: 16
            }
        },
        ...userModifiers
    ];

    return (
        <DropdownMenuPopper
            anchorEl={anchorRef?.current}
            className={clsx(
                className,
                dropdownCssClassNames.menu,
                'FuelDropdownMenu-root'
            )}
            disablePortal
            modifiers={popperModifiers}
            open={open}
            placement={resolvedPlacement}
            transition
        >
            {({
                TransitionProps, placement: childPlacement
            }): ReactElement => (
                <Grow
                    in={TransitionProps?.in}
                    onEnter={TransitionProps?.onEnter}
                    onExited={(): void => {
                        handleTransitionExited();
                        TransitionProps?.onExited();
                    }}
                    style={{
                        transformOrigin: transforms[childPlacement as FuelPlacement]
                    }}
                >
                    <div>{/** PLACEHOLDER for animation */}
                        <ClickAwayListener onClickAway={handleClose}>
                            <DropdownMenuWrapper
                                className={dropdownCssClassNames.menuWrapper}
                            >
                                <MenuList
                                    {...props}
                                    autoFocusItem={open}
                                    className={dropdownCssClassNames.menuList}
                                    hasDivider={hasDivider}
                                    id={dropdownMenuId}
                                    onKeyDown={handleListKeyDown}
                                >
                                    {children || Array.isArray(items) && items.map(({
                                        label, selected, subNavigationItems, FlyoutContent,
                                        ...itemProps
                                    }, itemIndex) => {
                                        const hasSubItems = Array.isArray(subNavigationItems) && Boolean(FlyoutContent);
                                        const isSelected = selected ?? (hasSubItems && menuIndex === itemIndex);

                                        return (
                                            <DropdownItem
                                                {...itemProps}
                                                className={clsx(
                                                    dropdownCssClassNames.menuItem,
                                                    'DropdownMenu-menuItem',
                                                    `DropdownMenu-menuItem${itemIndex}`
                                                )}
                                                disableClose={hasSubItems}
                                                endIcon={FlyoutContent && <KeyboardArrowRight/>}
                                                key={`menu-item-${itemIndex}`}
                                                onClick={(event): void => {
                                                    itemProps.onClick?.(event);

                                                    if (hasSubItems) {
                                                        setMenuIndex(itemIndex);
                                                    } else {
                                                        handleClose(event);
                                                    }
                                                }}
                                                selected={isSelected}
                                            >
                                                {label}
                                            </DropdownItem>
                                        );
                                    })}
                                </MenuList>
                                {renderFlyoutContent()}
                            </DropdownMenuWrapper>
                        </ClickAwayListener>
                    </div>
                </Grow>
            )}
        </DropdownMenuPopper>
    );
};
